/**
 * Accordion — универсальный компонент для сворачиваемых блоков с анимацией.
 *
 * Позволяет создать блок с заголовком, кастомной иконкой (слева или справа), а также плавным раскрытием/сворачиванием содержимого.
 * Поддерживает как управляемый, так и неуправляемый режим работы (controlled/uncontrolled).
 *
 * @component
 *
 * @example
 * // Неуправляемый режим (внутреннее управление состоянием)
 * <Accordion
 *   title="Подробнее"
 *   icon={<ChevronRightIcon />}
 *   defaultOpen={false}
 * >
 *   <div>Контент аккордеона</div>
 * </Accordion>
 *
 * @example
 * // Управляемый режим (контролируемый извне)
 * const [open, setOpen] = useState(false);
 * <Accordion
 *   title="Подробнее"
 *   icon={<ChevronRightIcon />}
 *   isOpen={open}
 *   onToggle={setOpen}
 * >
 *   <div>Контент аккордеона</div>
 * </Accordion>
 *
 * @param {React.ReactNode} title           — Заголовок аккордеона (текст или JSX-элемент)
 * @param {React.ReactNode} [icon]          — Иконка, отображаемая в заголовке (опционально)
 * @param {'start'|'end'}   [iconPosition]  — Расположение иконки: слева ('start') или справа ('end'). По умолчанию 'end'.
 * @param {boolean}         [defaultOpen]   — Открыт ли аккордеон по умолчанию (только для неуправляемого режима)
 * @param {boolean}         [isOpen]        — Состояние аккордеона (управляемый режим)
 * @param {(open:boolean)=>void} [onToggle] — Колбэк при изменении состояния (для управляемого режима)
 * @param {React.ReactNode} children        — Контент аккордеона, который будет скрываться/раскрываться
 * @param {string}          [className]     — Дополнительный CSS-класс для корневого элемента
 *
 * @returns {JSX.Element}
 */

import React, { SyntheticEvent, useLayoutEffect, useRef, useState } from 'react';

import cn from 'classnames';

import cls from './Accordion.module.css';

interface AccordionProps {
    title: React.ReactNode;
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
    defaultOpen?: boolean;
    isOpen?: boolean;
    onToggle?: (open: boolean) => void;
    children: React.ReactNode;
    className?: string;
    titleClass?: string;
    buttonClass?: string;
    openIconClassName?: string;
    contentClassName?: string;
    contentInnerClassName?: string;
}

export const Accordion = ({
    title,
    icon,
    iconPosition = 'end',
    defaultOpen = false,
    isOpen: controlledOpen,
    onToggle,
    children,
    className,
    titleClass,
    openIconClassName,
    contentClassName,
    contentInnerClassName,
    buttonClass,
}: AccordionProps) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const [maxHeight, setMaxHeight] = useState<string | number>(0);
    const contentRef = useRef<HTMLDivElement>(null);

    const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;

    useLayoutEffect(() => {
        if (isOpen && contentRef.current) {
            setMaxHeight(contentRef.current.scrollHeight);
        } else {
            setMaxHeight(0);
        }
    }, [isOpen, children]);

    const handleToggle = () => {
        if (controlledOpen !== undefined) {
            onToggle?.(!controlledOpen);
        } else {
            setUncontrolledOpen((prev) => !prev);
            onToggle?.(!uncontrolledOpen);
        }
    };

    return (
        <div
            className={cn(cls.accordion, className)}
            onClick={(e: SyntheticEvent) => e.stopPropagation()}
        >
            <button
                className={cn(cls.header, [buttonClass], { [cls.open]: isOpen })}
                onClick={handleToggle}
                type="button"
            >
                {icon && iconPosition === 'start' && (
                    <span
                        className={cn(
                            cls.icon,
                            { [cls.iconOpen]: isOpen },
                            { [openIconClassName]: isOpen }
                        )}
                        aria-hidden
                    >
                        {icon}
                    </span>
                )}
                <span className={cn(cls.title, titleClass)}>{title}</span>
                {icon && iconPosition === 'end' && (
                    <span
                        className={cn(
                            cls.icon,
                            { [cls.iconOpen]: isOpen },
                            { [openIconClassName]: isOpen }
                        )}
                        aria-hidden
                    >
                        {icon}
                    </span>
                )}
            </button>
            <div
                ref={contentRef}
                className={cn(cls.content, contentClassName)}
                style={{
                    maxHeight: isOpen ? `${maxHeight}px` : 0,
                    opacity: isOpen ? 1 : 0,
                }}
            >
                <div className={cn(cls.contentInner, contentInnerClassName)}>{children}</div>
            </div>
        </div>
    );
};
